import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AlertService } from '../../../_services/alert.service';
import { SupplierService } from '../../../_services/supplier.service';
import { Supplier } from '../../../_interfaces/supplier';
import { COUNTRIES, CountryOption, countryFlag, findCountry } from '../countries';

export interface QuickSupplierDialogData {
  // What was typed in the supplier search, to start the name with.
  name?: string;
  // Already-loaded suppliers, for the duplicate warning.
  suppliers?: Supplier[];
}

// Adds a supplier without leaving the page it's opened from (the purchase
// order form), so nothing typed there is lost. Closes with the saved
// Supplier, or null when cancelled. The full supplier page has the rest of
// the fields.
@Component({
    selector: 'app-quick-supplier-dialog',
    imports: [CommonModule, FormsModule, MatDialogContent, MatIconModule, MatTooltipModule, MatAutocompleteModule],
    templateUrl: './quick-supplier-dialog.component.html',
    styleUrl: '../../purchases/purchase-form.css'
})
export class QuickSupplierDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<QuickSupplierDialogComponent, Supplier | null>);

  name = '';
  email = '';
  phone = '';
  country = '';
  city = '';
  vatNumber = '';
  submitted = false;
  saving = false;
  countryFlag = countryFlag;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: QuickSupplierDialogData | null,
    private supplierService: SupplierService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.name = (this.data?.name || '').trim();
  }

  get emailInvalid(): boolean {
    return !!this.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
  }

  get duplicateSupplier(): Supplier | undefined {
    const n = this.name.trim().toLowerCase();
    return n ? (this.data?.suppliers ?? []).find((s) => s.name.trim().toLowerCase() === n) : undefined;
  }

  get countryMatch(): CountryOption | undefined {
    return findCountry(this.country);
  }

  get filteredCountries(): CountryOption[] {
    const q = this.country.trim().toLowerCase();
    if (!q || this.countryMatch?.name.toLowerCase() === q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
  }

  // Picks the existing supplier instead of creating a second one.
  useExisting(supplier: Supplier): void {
    this.dialogRef.close(supplier);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    this.submitted = true;
    if (!this.name.trim() || this.emailInvalid) return;
    this.saving = true;
    this.supplierService
      .createSupplier({
        name: this.name.trim(),
        email: this.email.trim() || undefined,
        phone: this.phone.trim() || undefined,
        country: this.country.trim() || undefined,
        city: this.city.trim() || undefined,
        vatNumber: this.vatNumber.trim() || undefined,
      })
      .subscribe({
        next: (supplier) => {
          this.saving = false;
          this.alertService.alert('Supplier created', 'success');
          this.dialogRef.close(supplier);
        },
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error?.error?.message || 'Could not create supplier', 'error');
        },
      });
  }
}
